import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import NotificationBellButton from '../../components/NotificationBellButton';
import { GuestBottomNav } from '../../components/GuestBottomNav';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const formatEuro = (value) => {
  const numeric = Number(value || 0);
  return Number.isInteger(numeric) ? `${numeric}` : numeric.toFixed(2).replace(/\.00$/, '');
};

const pillButtonStyle = {
  width: '100%',
  maxWidth: 280,
  border: 'none',
  borderRadius: 9999,
  background: '#04A628',
  color: '#FFFFFF',
  fontFamily: font,
  fontSize: 22,
  fontWeight: 700,
  padding: '13px 22px',
  cursor: 'pointer',
  boxShadow: '0 12px 24px rgba(4, 166, 40, 0.22)',
};

const securityCardStyle = {
  ...hostCardStyle,
  width: '100%',
  padding: '16px 18px',
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  border: '1px solid rgba(148, 163, 184, 0.32)',
  boxShadow: 'none',
};

const ShieldIcon = () => (
  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M17 3L28 7V15C28 22.2 23.3 28.73 17 31C10.7 28.73 6 22.2 6 15V7L17 3Z" fill="#F1FFF4" stroke="#31B94D" strokeWidth="2" />
    <path d="M12 17.2L15.4 20.6L22.4 13.6" stroke="#31B94D" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function GuestPostStayTipPage() {
  const navigate = useNavigate();
  const { guestPassId } = useParams();
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amountInput, setAmountInput] = useState('');
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [tipResult, setTipResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        const response = await axios.get(`${API}/post-stay-tips/guest-pass/${guestPassId}`, { headers });
        setPrompt(response.data);
      } catch (error) {
        setErrorMessage(error.response?.data?.detail || 'Unable to open this tip screen right now.');
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();
  }, [guestPassId, headers]);

  const parsedAmount = Number(amountInput || 0);
  const canSubmit = parsedAmount > 0 && !submitting;

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setAmountInput(String(amount));
  };

  const handleInputChange = (value) => {
    setSelectedAmount(null);
    setAmountInput(value.replace(/[^0-9.]/g, ''));
  };

  const handleSendTip = async () => {
    if (!canSubmit) {
      return;
    }

    setSubmitting(true);
    try {
      const minDelay = new Promise((resolve) => window.setTimeout(resolve, 1400));
      const request = axios.post(
        `${API}/post-stay-tips/guest-pass/${guestPassId}/submit`,
        { amount: parsedAmount },
        { headers },
      );
      const [response] = await Promise.all([request, minDelay]);
      if (navigator.vibrate) {
        navigator.vibrate([16, 42, 16]);
      }
      setTipResult(response.data);
      toast.success('Tip sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to send your tip right now');
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      await axios.post(`${API}/post-stay-tips/guest-pass/${guestPassId}/skip`, {}, { headers });
      navigate('/guest/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to skip tipping right now');
      setSkipping(false);
    }
  };

  if (loading) {
    return (
      <div className="app-shell" data-testid="guest-post-stay-tip-loading-page" style={{ backgroundColor: 'var(--surface)' }}>
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, paddingTop: 0 }}>
          <RouteTransitionLogo width={140} height={125} dataTestId="guest-post-stay-tip-loading-shared-logo" />
          <div data-testid="guest-post-stay-tip-spinner" className="animate-spin" style={{ width: 40, height: 40, borderRadius: '50%', border: '4px solid rgba(112, 63, 246, 0.12)', borderTopColor: '#703FF6' }} />
        </div>
        <GuestBottomNav />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="app-shell" data-testid="guest-post-stay-tip-error-page" style={{ backgroundColor: 'var(--surface)' }}>
        <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <NotificationBellButton buttonTestId="guest-post-stay-tip-error-bell-button" popupTestId="guest-post-stay-tip-error-notification-popup" />
        </div>
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, paddingTop: 0 }}>
          <RouteTransitionLogo width={160} height={140} dataTestId="guest-post-stay-tip-error-shared-logo" />
          <p data-testid="guest-post-stay-tip-error-message" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', maxWidth: 320, lineHeight: 1.35, margin: 0 }}>
            {errorMessage}
          </p>
          <button
            type="button"
            data-testid="guest-post-stay-tip-error-done-button"
            onClick={() => navigate('/guest/home')}
            style={pillButtonStyle}
          >
            Done
          </button>
        </div>
        <GuestBottomNav />
      </div>
    );
  }

  if (submitting && !tipResult) {
    return (
      <div className="app-shell" data-testid="guest-post-stay-tip-processing-page" style={{ backgroundColor: 'var(--surface)' }}>
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, paddingTop: 0 }}>
          <RouteTransitionLogo width={170} height={150} dataTestId="guest-post-stay-tip-processing-shared-logo" />
          <motion.div
            data-testid="guest-post-stay-tip-processing-loader"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
            style={{ width: 58, height: 58, borderRadius: '50%', border: '5px solid rgba(112, 63, 246, 0.14)', borderTopColor: '#703FF6' }}
          />
          <p data-testid="guest-post-stay-tip-processing-text" style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            Sending your tip…
          </p>
        </div>
        <GuestBottomNav />
      </div>
    );
  }

  if (tipResult) {
    return (
      <div className="app-shell" data-testid="guest-post-stay-tip-success-page" style={{ backgroundColor: 'var(--surface)' }}>
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, paddingTop: 0 }}>
          <RouteTransitionLogo width={170} height={150} dataTestId="guest-post-stay-tip-success-shared-logo" />
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [0.5, 1.12, 1], opacity: 1, y: [0, -8, 0] }}
            transition={{ duration: 0.8, times: [0, 0.7, 1] }}
            data-testid="guest-post-stay-tip-success-checkmark"
            style={{ width: 120, height: 120, borderRadius: '50%', background: '#08B62A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Check size={62} color="#FFFFFF" strokeWidth={4} />
          </motion.div>
          <p data-testid="guest-post-stay-tip-success-title" style={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.15, margin: 0, maxWidth: 330 }}>
            Your tip was sent to your host {tipResult.host_name} ❤️
          </p>
          <p data-testid="guest-post-stay-tip-success-amount" style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0 }}>
            {formatEuro(tipResult.amount)}€ sent successfully
          </p>
          <p data-testid="guest-post-stay-tip-success-description" style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.3, margin: 0, maxWidth: 340 }}>
            Thank you for supporting great hosts and local experiences.
          </p>
          <button
            type="button"
            data-testid="guest-post-stay-tip-done-button"
            onClick={() => navigate('/guest/home')}
            style={pillButtonStyle}
          >
            Done
          </button>
        </div>
        <GuestBottomNav />
      </div>
    );
  }

  return (
    <div className="app-shell" data-testid="guest-post-stay-tip-page" style={{ backgroundColor: 'var(--surface)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <NotificationBellButton buttonTestId="guest-post-stay-tip-bell-button" popupTestId="guest-post-stay-tip-notification-popup" />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, paddingTop: 0 }}>
        <RouteTransitionLogo width={168} height={148} dataTestId="guest-post-stay-tip-shared-logo" />

        <p data-testid="guest-post-stay-tip-title" style={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.12, margin: 0, maxWidth: 330 }}>
          Show your appreciation to your host {prompt.host_name}!
        </p>

        <p data-testid="guest-post-stay-tip-description" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.24, margin: '2px 0 0', maxWidth: 330 }}>
          Many guests leave a tip to thank their host for their hospitality and the Allure experience.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <div style={{ ...hostCardStyle, minWidth: 120, padding: '14px 18px', textAlign: 'center' }}>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={amountInput}
              onChange={(event) => handleInputChange(event.target.value)}
              placeholder=""
              data-testid="guest-post-stay-tip-amount-input"
              style={{ width: '100%', border: 'none', outline: 'none', textAlign: 'center', fontFamily: font, fontSize: 24, color: 'var(--text)', background: 'transparent' }}
            />
          </div>
          <span data-testid="guest-post-stay-tip-currency-symbol" style={{ fontFamily: font, fontSize: 28, color: 'var(--text-2)' }}>€</span>
        </div>

        <p data-testid="guest-post-stay-tip-range-text" style={{ fontFamily: font, fontSize: 18, color: 'var(--text-2)', textAlign: 'center', margin: '-2px 0 0' }}>
          Most guests tip between {formatEuro(prompt.min_suggested_amount)}€–{formatEuro(prompt.max_suggested_amount)}€
        </p>

        <div style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
          <p data-testid="guest-post-stay-tip-quick-selection-label" style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', textAlign: 'center', margin: 0 }}>
            Quick selection
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
            {prompt.amount_suggestions.map((amount) => {
              const isSelected = Number(selectedAmount) === Number(amount);
              return (
                <button
                  key={amount}
                  type="button"
                  data-testid={`guest-post-stay-tip-quick-${formatEuro(amount)}-button`}
                  onClick={() => handleAmountSelect(amount)}
                  style={{
                    ...hostCardStyle,
                    border: isSelected ? '1.5px solid rgba(112, 63, 246, 0.42)' : '1px solid rgba(112, 63, 246, 0.12)',
                    background: 'var(--surface)',
                    padding: '10px 0',
                    fontFamily: font,
                    fontSize: 22,
                    fontWeight: 700,
                    color: 'var(--text)',
                    cursor: 'pointer',
                    transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                    transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
                  }}
                >
                  {formatEuro(amount)}€
                </button>
              );
            })}
          </div>
        </div>

        <motion.button
          type="button"
          whileTap={{ scale: 0.98 }}
          data-testid="guest-post-stay-tip-submit-button"
          disabled={!canSubmit}
          onClick={handleSendTip}
          style={{ ...pillButtonStyle, opacity: canSubmit ? 1 : 0.55, marginTop: 10 }}
        >
          Send Tip
        </motion.button>

        <button
          type="button"
          data-testid="guest-post-stay-tip-skip-button"
          disabled={skipping}
          onClick={handleSkip}
          style={{ background: 'none', border: 'none', color: 'var(--text-2)', textDecoration: 'underline', fontFamily: font, fontSize: 20, cursor: skipping ? 'default' : 'pointer', marginTop: -4 }}
        >
          Skip tipping
        </button>

        <div data-testid="guest-post-stay-tip-security-card" style={securityCardStyle}>
          <ShieldIcon />
          <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text)', lineHeight: 1.2, margin: 0 }}>
            Powered by Viva Wallet - Your payments are securely processed and encrypted
          </p>
        </div>
      </div>

      <GuestBottomNav />
    </div>
  );
}